"use client";

import React from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Users, ShoppingBag, ArrowRight } from "lucide-react";
import { NavItemChild } from "./types";

interface ProductsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  pathname: string;
  items?: NavItemChild[];
}

const ProductsDropdown: React.FC<ProductsDropdownProps> = ({
  isOpen,
  onClose,
  pathname,
}) => {
  const isMembaly = pathname.startsWith("/membaly");
  const isTimeless = pathname.startsWith("/timeless-marketing");
  const isOverview = pathname === "/product-services";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 z-[150]"
        >
          <div className="w-[320px] bg-white rounded-2xl p-2 border border-slate-100 shadow-xl shadow-slate-900/10">
            {/* Membaly */}
            <Link
              href="/membaly"
              onClick={onClose}
              className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                isMembaly
                  ? "bg-slate-100 text-[#056686]"
                  : "text-[#373A74] hover:bg-slate-50"
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-[#68ab3a] flex items-center justify-center shrink-0">
                <Users size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] leading-tight">
                  Membaly
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5 leading-tight truncate">
                  Club Management Platform
                </div>
              </div>
            </Link>

            {/* Timeless Marketing */}
            <Link
              href="/timeless-marketing"
              onClick={onClose}
              className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                isTimeless
                  ? "bg-slate-100 text-[#056686]"
                  : "text-[#373A74] hover:bg-slate-50"
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#056686] flex items-center justify-center shrink-0">
                <ShoppingBag size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] leading-tight">
                  Timeless Marketing
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5 leading-tight truncate">
                  AI E-Commerce & Merch
                </div>
              </div>
            </Link>

            <div className="my-1 border-t border-slate-100" />

            {/* Overview / All Products */}
            <Link
              href="/product-services"
              onClick={onClose}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-[12.5px] transition-colors ${
                isOverview
                  ? "text-[#056686] font-semibold bg-slate-50"
                  : "text-slate-600 hover:text-[#056686] hover:bg-slate-50"
              }`}
            >
              <span>All AI Products & Services</span>
              <ArrowRight size={13} className="text-slate-400" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductsDropdown;
